package io.github.retar.portfolio.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initPassword_managers_compose_webPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("password_managers_compose_web.md", FrontMatterElement.Builder { addScalar("title", "Password managers can't see a canvas: real sign-in forms in Compose for the web"); addScalar("description", "Compose Multiplatform draws its text fields on a canvas, so browser password managers have nothing to fill or save. How Parrot slides a real HTML form under the canvas without changing how the fields look, from sign-in to new-password forms, and the focus fight, Tab order and font path it took to get there."); addScalar("cover", "/articles/password_managers_web.jpeg"); addScalar("date", "2026-10-25"); }))
}

@Page("/blog/password-managers-compose-multiplatform-web")
@Layout(".pages.MarkdownPage")
@Composable
fun Password_managers_compose_webPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("password-managers-can-t-see-a-canvas-real-sign-in-forms-in-compose-for-the-web") }) {
        org.jetbrains.compose.web.dom.Text("Password managers can't see a canvas: real sign-in forms in Compose for the web")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Parrot runs in a browser now, through Kotlin/Wasm and Compose Multiplatform (")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/android-app-in-the-browser-kotlin-wasm") {
            org.jetbrains.compose.web.dom.Text("the first note on that")
        }
        org.jetbrains.compose.web.dom.Text("). One of the first things people do in it is connect their own book server, like Audiobookshelf or Storyteller, with a username and a password. On a phone the system's autofill offers the saved login. In the browser nothing happened: no key icon in the field, no \"Save password?\" after signing in, nothing for 1Password or Bitwarden to fill.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This note is about why, and the small piece of shared Kotlin that fixed it for every sign-in form without making those forms look any different.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("there-was-no-form") }) {
        org.jetbrains.compose.web.dom.Text("There was no form")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("I opened the production build on the \"Add server\" screen and searched the page for anything a password manager could use. With no field focused there was no ")
        io.github.retar.portfolio.components.widgets.InlineCode("""input""")
        org.jetbrains.compose.web.dom.Text(", no ")
        io.github.retar.portfolio.components.widgets.InlineCode("""textarea""")
        org.jetbrains.compose.web.dom.Text(" and no ")
        io.github.retar.portfolio.components.widgets.InlineCode("""form""")
        org.jetbrains.compose.web.dom.Text(" element anywhere, shadow roots included.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That's how Compose works on the web. It draws the whole UI, text fields included, onto one canvas. When a field gets focus, Compose creates a hidden element to receive keyboard input, and in Parrot's build that element was this:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""<span contenteditable="true" class="compose-backing-field" autocomplete="off"></span>
""", lang = "html")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It has no ")
        io.github.retar.portfolio.components.widgets.InlineCode("""type""")
        org.jetbrains.compose.web.dom.Text(", no ")
        io.github.retar.portfolio.components.widgets.InlineCode("""name""")
        org.jetbrains.compose.web.dom.Text(", it isn't in a form, and it disappears when the field loses focus. A password manager looks for a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""<form>""")
        org.jetbrains.compose.web.dom.Text(" with a username input and an ")
        io.github.retar.portfolio.components.widgets.InlineCode("""<input type="password">""")
        org.jetbrains.compose.web.dom.Text(", then watches for that form to be submitted. Parrot gave it neither.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Labels and accessibility hints don't help here. The password manager isn't reading the screen. It's reading the DOM, and the DOM had nothing in it.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-idea-real-inputs-compose-decoration") }) {
        org.jetbrains.compose.web.dom.Text("The idea: real inputs, Compose decoration")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The obvious fix is an HTML form drawn over the Compose UI. That's also the obvious way to get it wrong. Parrot's text fields have their own look: the fill, the border, the focus outline, the error state, a \"Show password\" button, Day and Night themes. An HTML form on top would need all of that rebuilt in CSS, and it would cover every dialog, sheet and tooltip that Compose draws in the same place.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The reader had the same problem, and I'd already solved it there. The HTML goes ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("under")
        }
        org.jetbrains.compose.web.dom.Text(" the canvas, and the canvas is cleared in the one spot where the HTML should show through. So the plan for sign-in forms was:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Compose keeps drawing every field exactly as before: the box, the border, the outline, the error text.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Where the field's ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("text")
            }
            org.jetbrains.compose.web.dom.Text(" would be, it draws a transparent hole.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("A real ")
            io.github.retar.portfolio.components.widgets.InlineCode("""<input>""")
            org.jetbrains.compose.web.dom.Text(" sits under that hole, in the same font, size and colours.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Only the text and the caret belong to the browser. Everything around them is still the same Compose code that runs on Android and iPhone.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("one-shared-api-three-platforms") }) {
        org.jetbrains.compose.web.dom.Text("One shared API, three platforms")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Screens shouldn't need to know any of this. So the piece lives in Parrot's shared UI module as an ")
        io.github.retar.portfolio.components.widgets.InlineCode("""expect""")
        org.jetbrains.compose.web.dom.Text("/")
        io.github.retar.portfolio.components.widgets.InlineCode("""actual""")
        org.jetbrains.compose.web.dom.Text(" pair:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""@Composable
expect fun rememberCredentialForm(
    username: String,
    onUsernameChange: (String) -> Unit,
    password: String,
    onPasswordChange: (String) -> Unit,
    onSubmit: () -> Unit,
): CredentialForm

@Composable
expect fun CredentialFormArea(form: CredentialForm, spacing: Dp = 0.dp, inPopup: Boolean = false, content: @Composable () -> Unit)
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A screen wraps the rows that hold its two fields in ")
        io.github.retar.portfolio.components.widgets.InlineCode("""CredentialFormArea""")
        org.jetbrains.compose.web.dom.Text(", and its Sign in button calls ")
        io.github.retar.portfolio.components.widgets.InlineCode("""form.submit()""")
        org.jetbrains.compose.web.dom.Text(" instead of signing in directly. That's the whole change on the screen's side.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("On Android and iPhone both functions do nothing. The area just draws its content, and ")
        io.github.retar.portfolio.components.widgets.InlineCode("""submit()""")
        org.jetbrains.compose.web.dom.Text(" calls the screen's sign-in:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""actual fun CredentialFormArea(form: CredentialForm, spacing: Dp, inPopup: Boolean, content: @Composable () -> Unit) = content()
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("No reference image changed and no Android test was edited. The phones don't know the web version exists.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("On the web, ")
        io.github.retar.portfolio.components.widgets.InlineCode("""CredentialFormArea""")
        org.jetbrains.compose.web.dom.Text(" puts a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""WebElementView""")
        org.jetbrains.compose.web.dom.Text(" holding one ")
        io.github.retar.portfolio.components.widgets.InlineCode("""<form>""")
        org.jetbrains.compose.web.dom.Text(" under the rows. The fields find that form through a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""CompositionLocal""")
        org.jetbrains.compose.web.dom.Text(", and each one draws a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""Slot""")
        org.jetbrains.compose.web.dom.Text(" where its text used to go:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""Spacer(
    modifier
        .onGloballyPositioned { state.coordinates = it; state.look?.invoke() }
        // The canvas is cleared here, so the input under it shows through.
        .drawBehind { drawRect(Color.Transparent, blendMode = BlendMode.Clear) },
)
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every time the slot moves, Kotlin measures where it is relative to the form, converts that to CSS pixels and tells the page where to put the input, along with the font size, text colour, caret colour and background. So the input follows the field through scrolling and resizing, and switches between Day and Night with the rest of the screen.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The form itself is as plain as I could make it, because plain is what password managers recognise:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""form.method = "post"; // never a GET: a submit that got through would put the password in the address
const inputs = {
    username: make("username", "text", "username"),
    password: make("password", "password", "current-password"),
};
""", lang = "js")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Each input also gets the field's own label as its ")
        io.github.retar.portfolio.components.widgets.InlineCode("""aria-label""")
        org.jetbrains.compose.web.dom.Text(", and ")
        io.github.retar.portfolio.components.widgets.InlineCode("""aria-invalid""")
        org.jetbrains.compose.web.dom.Text(" in the error state.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("submitting-without-leaving-the-page") }) {
        org.jetbrains.compose.web.dom.Text("Submitting without leaving the page")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A password manager offers to save a login when it sees the form submitted. So Parrot's Sign in button can't just call a Kotlin function any more. It has to go through a real submit.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("On the web, ")
        io.github.retar.portfolio.components.widgets.InlineCode("""form.submit()""")
        org.jetbrains.compose.web.dom.Text(" calls ")
        io.github.retar.portfolio.components.widgets.InlineCode("""requestSubmit()""")
        org.jetbrains.compose.web.dom.Text(", which fires the browser's submit event. Parrot catches that event, cancels the navigation and runs the sign-in:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""form.addEventListener("submit", (event) => {
    event.preventDefault();
    onSubmit();
}, { capture: true });
""", lang = "js")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Enter in either input does the same, so the button and the keyboard take one path. After a successful sign-in the screen closes and the form leaves the page, in that order, which is the sequence password managers watch for.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Values go both ways too. Typing, pasting and a password manager's fill all fire ")
        io.github.retar.portfolio.components.widgets.InlineCode("""input""")
        org.jetbrains.compose.web.dom.Text(" or ")
        io.github.retar.portfolio.components.widgets.InlineCode("""change""")
        org.jetbrains.compose.web.dom.Text(", and those call the screen's setters. A password manager often fills a field ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("without")
        }
        org.jetbrains.compose.web.dom.Text(" focusing it, so listening for focus or key presses alone would miss the fill. In the other direction, when the app sets a value (a saved server's username, for example), it's written into the input whenever the two differ.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("where-it-fought-back") }) {
        org.jetbrains.compose.web.dom.Text("Where it fought back")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The layout part worked on the first try. The parts after that didn't.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("focus-bounced-between-the-two-worlds") }) {
        org.jetbrains.compose.web.dom.Text("Focus bounced between the two worlds")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Compose and the browser each have their own idea of what's focused. My first version synced them both ways: Compose focus on the slot focused the input, and focus in the input focused the slot. But Compose's ")
        io.github.retar.portfolio.components.widgets.InlineCode("""WebElementView""")
        org.jetbrains.compose.web.dom.Text(" also takes Compose focus when something inside it gets DOM focus. So the input focused the view, the view moved focus off the slot, the slot refocused the input, and around it went. Tab went nowhere.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The fix was to make the view never focusable and keep one direction as the rule. Compose focusing the slot focuses the input. When a click puts focus in the input, Compose is only asked to follow, so that its own Tab order carries on from the right place.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("tab-skipped-the-inputs") }) {
        org.jetbrains.compose.web.dom.Text("Tab skipped the inputs")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Parrot's web app has its own screen-reader layer, which also handles Tab by walking Compose's accessibility tree. The inputs aren't in that tree, because they sit under the canvas. So Tab went from the server address straight past both inputs.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Now the Tab order puts each visible input in by ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("position")
        }
        org.jetbrains.compose.web.dom.Text(": before the first stop that's below it, or to its right on the same row. A second bug turned up later. With a dialog open over the form, Tab could land on an input under the dialog's scrim. Inputs covered by a popup are now skipped, and a form that is itself inside a popup stays in that popup's Tab loop.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("clicks-had-to-get-through-the-canvas") }) {
        org.jetbrains.compose.web.dom.Text("Clicks had to get through the canvas")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The canvas sits on top, so it receives every press. While Compose sees the pointer over a slot, and therefore knows nothing of its own is drawn there, it tells the page to let presses through to the input. Pass-through ends when the pointer leaves, a wheel turns, or the input is disabled or removed. That last case matters, because a disabled input may never send ")
        io.github.retar.portfolio.components.widgets.InlineCode("""pointerleave""")
        org.jetbrains.compose.web.dom.Text(", and without it the whole screen stopped taking clicks.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("the-font-loaded-from-the-wrong-address") }) {
        org.jetbrains.compose.web.dom.Text("The font loaded from the wrong address")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The inputs use Figtree, the same font as the Compose fields, loaded from the app's resources. ")
        io.github.retar.portfolio.components.widgets.InlineCode("""Res.getUri""")
        org.jetbrains.compose.web.dom.Text(" returned a path with the current route stuck in front, ")
        io.github.retar.portfolio.components.widgets.InlineCode("""/settings/servers./composeResources/...""")
        org.jetbrains.compose.web.dom.Text(", so the font failed and the inputs quietly fell back to the system font. The page script now keeps only the ")
        io.github.retar.portfolio.components.widgets.InlineCode("""composeResources/""")
        org.jetbrains.compose.web.dom.Text(" part and resolves it against the script's own address.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("signing-up-is-a-different-form") }) {
        org.jetbrains.compose.web.dom.Text("Signing up is a different form")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The first version covered signing in to your own servers. Writing this note made me notice the forms it didn't cover: Parrot Cloud's sign-in, its \"Create an account\" screen, the page that sets a new password after a reset link, and the sign-in sheet for a preset catalogue like Standard Ebooks. So those got the same treatment, and the shared piece grew three options:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                io.github.retar.portfolio.components.widgets.InlineCode("""emailUsername""")
            }
            org.jetbrains.compose.web.dom.Text(": Parrot Cloud signs in with an email, so the username input becomes ")
            io.github.retar.portfolio.components.widgets.InlineCode("""type="email"""")
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                io.github.retar.portfolio.components.widgets.InlineCode("""newPassword""")
            }
            org.jetbrains.compose.web.dom.Text(": on \"Create an account\" and the reset page, the password says ")
            io.github.retar.portfolio.components.widgets.InlineCode("""autocomplete="new-password"""")
            org.jetbrains.compose.web.dom.Text(". That's the signal for a password manager to offer a generated password instead of filling a saved one. Switching between Sign in and Create an account changes the attribute in place and keeps what was typed.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A repeat-password input")
            }
            org.jetbrains.compose.web.dom.Text(" (")
            io.github.retar.portfolio.components.widgets.InlineCode("""name="confirm-password"""")
            org.jetbrains.compose.web.dom.Text(", also ")
            io.github.retar.portfolio.components.widgets.InlineCode("""new-password""")
            org.jetbrains.compose.web.dom.Text("), so the reset page's \"New password again\" gets the same generated value.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The defaults keep the server forms exactly as they were, and on Android and iPhone the new parameters are accepted and ignored.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The Parrot Cloud screens don't use Parrot's own field component. They use Material 3's ")
        io.github.retar.portfolio.components.widgets.InlineCode("""OutlinedTextField""")
        org.jetbrains.compose.web.dom.Text(". So they got a small web-only field that draws Material's decoration (outline, label, colours, shape) around a slot instead of around Compose's own text. The first build drew those fields 4 dp short, with a doubled red line in the error state. The inner content wasn't being stretched to the decorator's minimum height, and ")
        io.github.retar.portfolio.components.widgets.InlineCode("""propagateMinConstraints""")
        org.jetbrains.compose.web.dom.Text(" fixed it. The check compared screenshots of the new fields against the old ones in Day and Night, and that's how this got caught.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("checking-it-without-a-password-manager") }) {
        org.jetbrains.compose.web.dom.Text("Checking it without a password manager")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("I didn't want \"it worked when I tried it\" to be the test, so there's a script that drives a production build in a headless browser. It checks seven things on the four server and catalogue sign-in forms. The Parrot Cloud forms get the first five:")
    }
    org.jetbrains.compose.web.dom.Ol {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Each form has both inputs with the right ")
            io.github.retar.portfolio.components.widgets.InlineCode("""name""")
            org.jetbrains.compose.web.dom.Text(", ")
            io.github.retar.portfolio.components.widgets.InlineCode("""type""")
            org.jetbrains.compose.web.dom.Text(", ")
            io.github.retar.portfolio.components.widgets.InlineCode("""autocomplete""")
            org.jetbrains.compose.web.dom.Text(" and ")
            io.github.retar.portfolio.components.widgets.InlineCode("""aria-label""")
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("A fill done the way a password manager does it, with values set, events fired and no focus, enables Sign in, and the sign-in reaches a test Audiobookshelf server.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Enter in the password signs in, and the page neither reloads nor navigates.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Tab and Shift+Tab visit address, username, password and Show password in order, with no trap.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Day and Night, empty, focused and error states all match, down to the computed caret colour.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("With a dialog open over the form, Tab stays inside the dialog.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("A disabled or removed input gives the clicks back to the page.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every new check was written to fail on the old code first, and all of them did.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One detail of the setup is worth knowing. Self-hosted servers like Audiobookshelf don't send CORS headers, so a web page on another address can't read their answers. The check puts a small local proxy in front of the test server that adds those headers and nothing else. That's the same thing Parrot's web app tells self-hosters to do.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("honest-status") }) {
        org.jetbrains.compose.web.dom.Text("Honest status")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every sign-in form in the web app now uses it: your own servers, catalogues, preset catalogue sheets, and Parrot Cloud's sign-in, account creation and password reset. Some things are still open:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("No real password manager in the automated checks.")
            }
            org.jetbrains.compose.web.dom.Text(" The script fills fields the way one does, but that isn't the same as running 1Password, Bitwarden or a browser's own manager. Trying them by hand is the next step.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("The text looks slightly different.")
            }
            org.jetbrains.compose.web.dom.Text(" The browser draws typed text and password dots a little heavier than Compose's canvas did, in the same font and size. That's browser text rendering, and I've left it.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("It leans on Compose internals.")
            }
            org.jetbrains.compose.web.dom.Text(" Finding the canvas depends on how Compose 1.13 nests its HTML layer, the same shortcut the reader takes. The code marks it, and it's the first thing to recheck after a Compose update.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("What surprised me is how little of this was about passwords. The form itself is about ten lines of HTML. Everything else came from two UI systems sharing one screen: whose focus it is, whose click it is, whose Tab it is. Compose on the web is a canvas pretending to be a page, and a password manager is one of the few visitors that can tell.")
    }
}
